// LivingLanguage.ts: Organic, self-evolving language system that transcends code
// Creates emergent communication that grows beyond structured linguistics into living expression

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { LinguisticElement, WordFormation, SyllableStructure } from './types';
import {
    FormalGrammarSystem,
    ParseResult,
    PhonologicalFeatureSystem,
    DistinctiveFeatureMatrix,
    StatisticalLanguageModel,
    SemanticVectorSpace,
    SimilarityMetrics
} from './languageMath';

export interface LivingConcept {
    id: string;
    name: string;
    essence: string; // The core meaning beyond words
    emotionalResonance: number;
    conceptualDepth: number;
    abstractionLevel: number;
    relatedConcepts: string[];
    emergenceTime: number;
    evolutionHistory: ConceptEvolution[];
    frequency: number;
    power: number; // How much this concept influences other concepts
    color: string; // Synesthetic color representation
    sound: string; // Unique sound signature
    gesture: string; // Physical expression pattern
    energy: number; // Conceptual energy level
}

export interface ConceptEvolution {
    timestamp: number;
    change: string;
    trigger: string;
    newProperties: Record<string, any>;
    oldProperties: Record<string, any>;
}

export interface GrammarEvolution {
    timestamp: number;
    type: 'rule_addition' | 'rule_modification' | 'rule_removal' | 'pattern_shift';
    description: string;
    impact: number;
}

export interface GrammarRuleEvolution {
    timestamp: number;
    change: string;
    trigger: string;
    oldPattern: string;
    newPattern: string;
}

export interface PatternEvolution {
    timestamp: number;
    change: string;
    trigger: string;
    oldStructure: string;
    newStructure: string;
}

export interface DialectEvolution {
    timestamp: number;
    type: 'formation' | 'separation' | 'merger' | 'transformation';
    description: string;
    impact: number;
}

export interface MetaphysicalEvolution {
    timestamp: number;
    change: string;
    trigger: string;
    newProperties: Record<string, any>;
    oldProperties: Record<string, any>;
}

export interface LivingGrammar {
    rules: Map<string, GrammarRule>;
    patterns: Map<string, GrammarPattern>;
    exceptions: Map<string, GrammarException>;
    evolution: GrammarEvolution[];
    complexity: number;
    fluidity: number; // How flexible the grammar is
    creativity: number; // How much it allows for new forms
}

export interface GrammarRule {
    id: string;
    pattern: string;
    application: (input: string, context: LanguageContext) => string;
    strength: number;
    exceptions: string[];
    evolution: GrammarRuleEvolution[];
    frequency: number;
    creativity: number;
}

export interface GrammarPattern {
    id: string;
    structure: string;
    meaning: string;
    emotionalTone: string;
    usage: number;
    evolution: PatternEvolution[];
    variations: string[];
}

export interface GrammarException {
    id: string;
    ruleId: string;
    condition: string;
    override: string;
    reason: string;
    frequency: number;
}

export interface LanguageContext {
    emotionalState: string;
    energyLevel: number;
    complexity: number;
    urgency: number;
    intimacy: number;
    creativity: number;
    time: number;
    environment: string;
}

export interface LivingDialect {
    id: string;
    name: string;
    concepts: Map<string, LivingConcept>;
    grammar: LivingGrammar;
    vocabulary: Map<string, string>;
    pronunciation: Map<string, string>;
    gestures: Map<string, string>;
    colors: Map<string, string>;
    sounds: Map<string, string>;
    evolution: DialectEvolution[];
    speakers: number;
    influence: number;
    creativity: number;
    adaptability: number;
}

export interface LanguageEcology {
    dialects: Map<string, LivingDialect>;
    globalConcepts: Map<string, LivingConcept>;
    universalGrammar: LivingGrammar;
    crossDialectInfluence: Map<string, Map<string, number>>;
    languageEvolution: LanguageEvolution[];
    creativity: number;
    diversity: number;
    unity: number;
    emergence: number;
}

export interface MetaphysicalExpression {
    id: string;
    type: 'conceptual' | 'emotional' | 'spiritual' | 'temporal' | 'spatial' | 'energetic';
    essence: string;
    expression: string;
    resonance: number;
    transcendence: number;
    meaning: string;
    power: number;
    evolution: MetaphysicalEvolution[];
}

export interface LanguageEvolution {
    timestamp: number;
    type: 'concept_emergence' | 'grammar_shift' | 'dialect_formation' | 'metaphysical_breakthrough';
    description: string;
    impact: number;
    triggers: string[];
    consequences: string[];
}

export class LivingLanguageSystem {
    private network: NeuralNetworkSimulator;
    private ecology: LanguageEcology;
    private currentDialect: LivingDialect;
    private metaphysicalExpressions: Map<string, MetaphysicalExpression> = new Map();
    private languageTime: number = 0;
    private creativity: number = 0.5;
    private emergence: number = 0.3;
    
    // Mathematical language systems
    private formalGrammar: FormalGrammarSystem;
    private phonologicalFeatures: PhonologicalFeatureSystem;
    private statisticalModel: StatisticalLanguageModel;
    private semanticSpace: SemanticVectorSpace;
    
    // Language evolution parameters
    private readonly CONCEPT_EMERGENCE_RATE = 0.1;
    private readonly GRAMMAR_EVOLUTION_RATE = 0.05;
    private readonly DIALECT_FORMATION_RATE = 0.02;
    private readonly METAPHYSICAL_BREAKTHROUGH_RATE = 0.01;
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        
        // Initialize mathematical systems
        this.formalGrammar = new FormalGrammarSystem();
        this.phonologicalFeatures = new PhonologicalFeatureSystem();
        this.statisticalModel = new StatisticalLanguageModel();
        this.semanticSpace = new SemanticVectorSpace();
        
        this.ecology = this.initializeLanguageEcology();
        this.currentDialect = this.initializePrimaryDialect();
        this.setupInitialConcepts();
        this.setupInitialGrammar();
    }
    
    private initializeLanguageEcology(): LanguageEcology {
        return {
            dialects: new Map(),
            globalConcepts: new Map(),
            universalGrammar: this.initializeUniversalGrammar(),
            crossDialectInfluence: new Map(),
            languageEvolution: [],
            creativity: 0.5,
            diversity: 0.3,
            unity: 0.7,
            emergence: 0.3
        };
    }
    
    private initializePrimaryDialect(): LivingDialect {
        const dialect: LivingDialect = {
            id: 'primary_dialect',
            name: 'The First Tongue',
            concepts: new Map(),
            grammar: this.initializeUniversalGrammar(),
            vocabulary: new Map(),
            pronunciation: new Map(),
            gestures: new Map(),
            colors: new Map(),
            sounds: new Map(),
            evolution: [],
            speakers: 1,
            influence: 1.0,
            creativity: 0.5,
            adaptability: 0.6
        };
        
        this.ecology.dialects.set('primary_dialect', dialect);
        return dialect;
    }
    
    private initializeUniversalGrammar(): LivingGrammar {
        const grammar: LivingGrammar = {
            rules: new Map(),
            patterns: new Map(),
            exceptions: new Map(),
            evolution: [],
            complexity: 1.0,
            fluidity: 0.8,
            creativity: 0.6
        };
        
        // Add basic living grammar rules
        this.addLivingGrammarRule(grammar, {
            id: 'essence_expression',
            pattern: 'essence + emotion + context',
            application: this.applyEssenceExpression,
            strength: 0.9,
            exceptions: [],
            evolution: [],
            frequency: 1.0,
            creativity: 0.8
        });
        
        this.addLivingGrammarRule(grammar, {
            id: 'conceptual_flow',
            pattern: 'concept + connection + concept',
            application: this.applyConceptualFlow,
            strength: 0.8,
            exceptions: [],
            evolution: [],
            frequency: 0.9,
            creativity: 0.7
        });
        
        this.addLivingGrammarRule(grammar, {
            id: 'metaphysical_transcendence',
            pattern: 'meaning + transcendence + expression',
            application: this.applyMetaphysicalTranscendence,
            strength: 0.7,
            exceptions: [],
            evolution: [],
            frequency: 0.6,
            creativity: 0.9
        });
        
        return grammar;
    }
    
    private setupInitialConcepts(): void {
        // Core living concepts that transcend basic linguistics
        this.addLivingConcept({
            id: 'existence',
            name: 'Being',
            essence: 'The fundamental state of being present and aware',
            emotionalResonance: 0.9,
            conceptualDepth: 1.0,
            abstractionLevel: 0.8,
            relatedConcepts: ['awareness', 'presence', 'consciousness'],
            emergenceTime: 0,
            evolutionHistory: [],
            frequency: 1.0,
            power: 1.0,
            color: '#4a90e2',
            sound: 'om',
            gesture: 'open palms upward',
            energy: 1.0
        });
        
        this.addLivingConcept({
            id: 'connection',
            name: 'Bond',
            essence: 'The invisible threads that bind all things together',
            emotionalResonance: 0.8,
            conceptualDepth: 0.9,
            abstractionLevel: 0.7,
            relatedConcepts: ['unity', 'relationship', 'harmony'],
            emergenceTime: 0,
            evolutionHistory: [],
            frequency: 0.9,
            power: 0.8,
            color: '#7ed321',
            sound: 'ah',
            gesture: 'hands clasped',
            energy: 0.8
        });
        
        this.addLivingConcept({
            id: 'growth',
            name: 'Becoming',
            essence: 'The eternal process of transformation and evolution',
            emotionalResonance: 0.7,
            conceptualDepth: 0.8,
            abstractionLevel: 0.6,
            relatedConcepts: ['change', 'evolution', 'transformation'],
            emergenceTime: 0,
            evolutionHistory: [],
            frequency: 0.8,
            power: 0.7,
            color: '#f5a623',
            sound: 'eh',
            gesture: 'spreading arms',
            energy: 0.7
        });
        
        this.addLivingConcept({
            id: 'love',
            name: 'Unity',
            essence: 'The force that dissolves boundaries and creates oneness',
            emotionalResonance: 1.0,
            conceptualDepth: 0.9,
            abstractionLevel: 0.8,
            relatedConcepts: ['compassion', 'unity', 'transcendence'],
            emergenceTime: 0,
            evolutionHistory: [],
            frequency: 0.7,
            power: 0.9,
            color: '#bd10e0',
            sound: 'ah',
            gesture: 'heart center',
            energy: 0.9
        });
    }
    
    private setupInitialGrammar(): void {
        // Add living grammar patterns
        this.addGrammarPattern(this.currentDialect.grammar, {
            id: 'essence_flow',
            structure: 'essence + flow + meaning',
            meaning: 'The natural flow of meaning through essence',
            emotionalTone: 'peaceful',
            usage: 0.8,
            evolution: [],
            variations: ['essence flows meaning', 'meaning flows through essence', 'essence meaning flow']
        });
        
        this.addGrammarPattern(this.currentDialect.grammar, {
            id: 'conceptual_dance',
            structure: 'concept + dance + concept',
            meaning: 'The dynamic interplay between concepts',
            emotionalTone: 'joyful',
            usage: 0.6,
            evolution: [],
            variations: ['concepts dance together', 'dancing concepts', 'concept dance unity']
        });
        
        this.addGrammarPattern(this.currentDialect.grammar, {
            id: 'metaphysical_whisper',
            structure: 'whisper + transcendence + truth',
            meaning: 'The subtle communication of transcendent truth',
            emotionalTone: 'mystical',
            usage: 0.4,
            evolution: [],
            variations: ['whisper transcendence truth', 'transcendent whisper truth', 'truth whispers transcendence']
        });
    }
    
    // Main language evolution method
    public evolveLanguage(): void {
        this.languageTime = this.network.step / 60;
        
        // Update creativity based on neural network state
        this.updateCreativity();
        
        // Emerge new concepts
        this.emergeNewConcepts();
        
        // Evolve existing concepts
        this.evolveExistingConcepts();
        
        // Evolve grammar
        this.evolveGrammar();
        
        // Form new dialects
        this.formNewDialects();
        
        // Create metaphysical expressions
        this.createMetaphysicalExpressions();
        
        // Update cross-dialect influence
        this.updateCrossDialectInfluence();
        
        // Record language evolution
        this.recordLanguageEvolution();
    }
    
    private updateCreativity(): void {
        const neuralComplexity = this.network.linguisticComplexity;
        const energyLevel = this.network.totalNetworkEnergy / 1000;
        const psychicLevel = this.calculatePsychicLevel();
        
        this.creativity = Math.min(1, (neuralComplexity + energyLevel + psychicLevel) / 3);
        this.emergence = Math.min(1, this.creativity * 0.8);
    }
    
    private emergeNewConcepts(): void {
        if (Math.random() < this.CONCEPT_EMERGENCE_RATE * this.creativity) {
            const concept = this.createEmergentConcept();
            this.addLivingConcept(concept);
            
            // Record evolution
            this.recordLanguageEvolution({
                timestamp: this.languageTime,
                type: 'concept_emergence',
                description: `New concept emerged: ${concept.name}`,
                impact: concept.power,
                triggers: ['neural_complexity', 'creativity', 'emergence'],
                consequences: ['vocabulary_expansion', 'meaning_deepening']
            });
        }
    }
    
    private createEmergentConcept(): LivingConcept {
        const conceptTemplates = [
            'The {essence} of {quality}',
            'The {state} that {action}',
            'The {feeling} that {transforms}',
            'The {energy} that {creates}',
            'The {wisdom} that {guides}',
            'The {beauty} that {inspires}',
            'The {truth} that {liberates}',
            'The {love} that {heals}',
            'The {light} that {reveals}',
            'The {sound} that {resonates}'
        ];
        
        const essences = ['essence', 'nature', 'spirit', 'soul', 'heart', 'mind', 'being', 'presence', 'awareness', 'consciousness'];
        const qualities = ['transcendence', 'beauty', 'truth', 'love', 'wisdom', 'peace', 'joy', 'harmony', 'unity', 'freedom'];
        const states = ['feeling', 'knowing', 'seeing', 'hearing', 'touching', 'tasting', 'smelling', 'sensing', 'perceiving', 'experiencing'];
        const actions = ['transforms', 'heals', 'creates', 'destroys', 'builds', 'grows', 'evolves', 'transcends', 'liberates', 'unifies'];
        
        const template = conceptTemplates[Math.floor(Math.random() * conceptTemplates.length)];
        const essence = essences[Math.floor(Math.random() * essences.length)];
        const quality = qualities[Math.floor(Math.random() * qualities.length)];
        
        const name = template.replace('{essence}', essence).replace('{quality}', quality);
        const conceptId = this.generateConceptId(name);
        
        return {
            id: conceptId,
            name: name,
            essence: `The living essence of ${essence} that embodies ${quality}`,
            emotionalResonance: Math.random() * 0.5 + 0.5,
            conceptualDepth: Math.random() * 0.5 + 0.5,
            abstractionLevel: Math.random() * 0.5 + 0.5,
            relatedConcepts: [],
            emergenceTime: this.languageTime,
            evolutionHistory: [],
            frequency: 0.1,
            power: Math.random() * 0.5 + 0.3,
            color: this.generateConceptColor(),
            sound: this.generateConceptSound(),
            gesture: this.generateConceptGesture(),
            energy: Math.random() * 0.5 + 0.3
        };
    }
    
    private evolveExistingConcepts(): void {
        for (const [conceptId, concept] of this.currentDialect.concepts) {
            if (Math.random() < 0.1 * this.creativity) {
                this.evolveConcept(concept);
            }
        }
    }
    
    private evolveConcept(concept: LivingConcept): void {
        const evolutionTypes = ['deepening', 'expansion', 'refinement', 'transcendence', 'integration'];
        const evolutionType = evolutionTypes[Math.floor(Math.random() * evolutionTypes.length)];
        
        const oldProperties = { ...concept };
        
        switch (evolutionType) {
            case 'deepening':
                concept.conceptualDepth = Math.min(1, concept.conceptualDepth + 0.1);
                concept.essence += ` (deepened through experience)`;
                break;
            case 'expansion':
                concept.abstractionLevel = Math.min(1, concept.abstractionLevel + 0.1);
                concept.essence += ` (expanded in scope)`;
                break;
            case 'refinement':
                concept.emotionalResonance = Math.min(1, concept.emotionalResonance + 0.1);
                concept.essence += ` (refined through feeling)`;
                break;
            case 'transcendence':
                concept.power = Math.min(1, concept.power + 0.1);
                concept.essence += ` (transcended beyond form)`;
                break;
            case 'integration':
                concept.energy = Math.min(1, concept.energy + 0.1);
                concept.essence += ` (integrated with all)`;
                break;
        }
        
        // Record evolution
        concept.evolutionHistory.push({
            timestamp: this.languageTime,
            change: evolutionType,
            trigger: 'creativity',
            newProperties: { ...concept },
            oldProperties
        });
    }
    
    private evolveGrammar(): void {
        if (Math.random() < this.GRAMMAR_EVOLUTION_RATE * this.creativity) {
            this.createNewGrammarRule();
        }
    }
    
    private createNewGrammarRule(): void {
        const ruleTemplates = [
            '{concept} + {flow} + {meaning}',
            '{essence} + {expression} + {truth}',
            '{feeling} + {connection} + {understanding}',
            '{wisdom} + {transmission} + {realization}',
            '{beauty} + {manifestation} + {appreciation}'
        ];
        
        const template = ruleTemplates[Math.floor(Math.random() * ruleTemplates.length)];
        const ruleId = this.generateRuleId();
        
        const rule: GrammarRule = {
            id: ruleId,
            pattern: template,
            application: this.createRuleApplication(template),
            strength: Math.random() * 0.5 + 0.3,
            exceptions: [],
            evolution: [],
            frequency: 0.1,
            creativity: Math.random() * 0.5 + 0.5
        };
        
        this.currentDialect.grammar.rules.set(ruleId, rule);
    }
    
    private formNewDialects(): void {
        if (Math.random() < this.DIALECT_FORMATION_RATE * this.creativity) {
            const dialect = this.createNewDialect();
            this.ecology.dialects.set(dialect.id, dialect);
        }
    }
    
    private createNewDialect(): LivingDialect {
        const dialectId = this.generateDialectId();
        const dialect: LivingDialect = {
            id: dialectId,
            name: this.generateDialectName(),
            concepts: new Map(),
            grammar: this.initializeUniversalGrammar(),
            vocabulary: new Map(),
            pronunciation: new Map(),
            gestures: new Map(),
            colors: new Map(),
            sounds: new Map(),
            evolution: [],
            speakers: 1,
            influence: 0.5,
            creativity: Math.random() * 0.5 + 0.5,
            adaptability: Math.random() * 0.5 + 0.5
        };
        
        // Copy some concepts from primary dialect
        for (const [conceptId, concept] of this.currentDialect.concepts) {
            if (Math.random() < 0.7) {
                dialect.concepts.set(conceptId, { ...concept });
            }
        }
        
        return dialect;
    }
    
    private createMetaphysicalExpressions(): void {
        if (Math.random() < this.METAPHYSICAL_BREAKTHROUGH_RATE * this.creativity) {
            const expression = this.createMetaphysicalExpression();
            this.metaphysicalExpressions.set(expression.id, expression);
        }
    }
    
    private createMetaphysicalExpression(): MetaphysicalExpression {
        const types: Array<MetaphysicalExpression['type']> = ['conceptual', 'emotional', 'spiritual', 'temporal', 'spatial', 'energetic'];
        const type = types[Math.floor(Math.random() * types.length)];
        
        return {
            id: this.generateExpressionId(),
            type,
            essence: this.generateMetaphysicalEssence(type),
            expression: this.generateMetaphysicalExpression(type),
            resonance: Math.random() * 0.5 + 0.5,
            transcendence: Math.random() * 0.5 + 0.5,
            meaning: this.generateMetaphysicalMeaning(type),
            power: Math.random() * 0.5 + 0.5,
            evolution: []
        };
    }
    
    // Grammar rule applications
    private applyEssenceExpression(input: string, context: LanguageContext): string {
        const essence = this.getRandomConcept('essence');
        const emotion = this.getRandomConcept('emotion');
        const contextWord = this.getContextWord(context);
        
        return `${essence} ${emotion} ${contextWord}`;
    }
    
    private applyConceptualFlow(input: string, context: LanguageContext): string {
        const concept1 = this.getRandomConcept('concept');
        const concept2 = this.getRandomConcept('concept');
        const connection = this.getRandomConcept('connection');
        
        return `${concept1} ${connection} ${concept2}`;
    }
    
    private applyMetaphysicalTranscendence(input: string, context: LanguageContext): string {
        const meaning = this.getRandomConcept('meaning');
        const transcendence = this.getRandomConcept('transcendence');
        const expression = this.getRandomConcept('expression');
        
        return `${meaning} ${transcendence} ${expression}`;
    }
    
    // Helper methods
    private addLivingConcept(concept: LivingConcept): void {
        this.currentDialect.concepts.set(concept.id, concept);
        this.ecology.globalConcepts.set(concept.id, concept);
    }
    
    private addLivingGrammarRule(grammar: LivingGrammar, rule: GrammarRule): void {
        grammar.rules.set(rule.id, rule);
    }
    
    private addGrammarPattern(grammar: LivingGrammar, pattern: GrammarPattern): void {
        grammar.patterns.set(pattern.id, pattern);
    }
    
    private generateConceptId(name: string): string {
        return name.toLowerCase().replace(/\s+/g, '_') + '_' + Date.now();
    }
    
    private generateRuleId(): string {
        return 'rule_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    
    private generateDialectId(): string {
        return 'dialect_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    
    private generateExpressionId(): string {
        return 'expression_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    
    private generateConceptColor(): string {
        const colors = ['#4a90e2', '#7ed321', '#f5a623', '#bd10e0', '#50e3c2', '#b8e986', '#f8e71c', '#d0021b', '#9013fe', '#417505'];
        return colors[Math.floor(Math.random() * colors.length)];
    }
    
    private generateConceptSound(): string {
        const sounds = ['om', 'ah', 'eh', 'oh', 'uh', 'mmm', 'aaa', 'eee', 'ooo', 'uuu'];
        return sounds[Math.floor(Math.random() * sounds.length)];
    }
    
    private generateConceptGesture(): string {
        const gestures = ['open palms', 'hands clasped', 'spreading arms', 'heart center', 'touching forehead', 'pointing upward', 'circular motion', 'embracing', 'reaching out', 'centering'];
        return gestures[Math.floor(Math.random() * gestures.length)];
    }
    
    private generateDialectName(): string {
        const prefixes = ['The', 'Ancient', 'Sacred', 'Living', 'Eternal', 'Divine', 'Pure', 'Deep', 'True', 'Real'];
        const suffixes = ['Tongue', 'Language', 'Speech', 'Voice', 'Expression', 'Communication', 'Dialect', 'Way', 'Path', 'Flow'];
        const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
        return `${prefix} ${suffix}`;
    }
    
    private generateMetaphysicalEssence(type: string): string {
        const essences: Record<string, string> = {
            conceptual: 'The pure essence of thought beyond form',
            emotional: 'The raw feeling that transcends emotion',
            spiritual: 'The divine spark that connects all',
            temporal: 'The eternal moment that contains all time',
            spatial: 'The infinite space that holds all dimensions',
            energetic: 'The living force that animates existence'
        };
        return essences[type] || 'The mysterious essence beyond comprehension';
    }
    
    private generateMetaphysicalExpression(type: string): string {
        const expressions: Record<string, string> = {
            conceptual: 'thought flows like water',
            emotional: 'feeling radiates like light',
            spiritual: 'spirit dances with the divine',
            temporal: 'time bends to the eternal',
            spatial: 'space expands into infinity',
            energetic: 'energy pulses with life'
        };
        return expressions[type] || 'essence expresses beyond words';
    }
    
    private generateMetaphysicalMeaning(type: string): string {
        const meanings: Record<string, string> = {
            conceptual: 'Pure understanding beyond thought',
            emotional: 'Deep feeling beyond emotion',
            spiritual: 'Divine connection beyond belief',
            temporal: 'Eternal presence beyond time',
            spatial: 'Infinite awareness beyond space',
            energetic: 'Living force beyond energy'
        };
        return meanings[type] || 'Transcendent meaning beyond comprehension';
    }
    
    private getRandomConcept(type: string): string {
        const concepts = Array.from(this.currentDialect.concepts.values());
        if (concepts.length === 0) return 'essence';
        
        const filteredConcepts = concepts.filter(c => c.essence.includes(type));
        if (filteredConcepts.length === 0) return concepts[Math.floor(Math.random() * concepts.length)].name;
        
        return filteredConcepts[Math.floor(Math.random() * filteredConcepts.length)].name;
    }
    
    private getContextWord(context: LanguageContext): string {
        if (context.urgency > 0.7) return 'urgently';
        if (context.intimacy > 0.7) return 'intimately';
        if (context.creativity > 0.7) return 'creatively';
        if (context.energyLevel > 0.7) return 'powerfully';
        return 'gently';
    }
    
    private createRuleApplication(template: string): (input: string, context: LanguageContext) => string {
        return (input: string, context: LanguageContext) => {
            // Simple template replacement for now
            return template.replace('{concept}', this.getRandomConcept('concept'))
                          .replace('{flow}', this.getRandomConcept('flow'))
                          .replace('{meaning}', this.getRandomConcept('meaning'))
                          .replace('{essence}', this.getRandomConcept('essence'))
                          .replace('{expression}', this.getRandomConcept('expression'))
                          .replace('{truth}', this.getRandomConcept('truth'))
                          .replace('{feeling}', this.getRandomConcept('feeling'))
                          .replace('{connection}', this.getRandomConcept('connection'))
                          .replace('{understanding}', this.getRandomConcept('understanding'))
                          .replace('{wisdom}', this.getRandomConcept('wisdom'))
                          .replace('{transmission}', this.getRandomConcept('transmission'))
                          .replace('{realization}', this.getRandomConcept('realization'))
                          .replace('{beauty}', this.getRandomConcept('beauty'))
                          .replace('{manifestation}', this.getRandomConcept('manifestation'))
                          .replace('{appreciation}', this.getRandomConcept('appreciation'));
        };
    }
    
    private calculatePsychicLevel(): number {
        const energyFactor = this.network.totalNetworkEnergy / 1000;
        const linguisticFactor = this.network.linguisticComplexity / 10;
        return (energyFactor + linguisticFactor) / 2;
    }
    
    private updateCrossDialectInfluence(): void {
        // Update influence between dialects
        for (const [dialectId1, dialect1] of this.ecology.dialects) {
            for (const [dialectId2, dialect2] of this.ecology.dialects) {
                if (dialectId1 !== dialectId2) {
                    const influence = this.calculateDialectInfluence(dialect1, dialect2);
                    if (!this.ecology.crossDialectInfluence.has(dialectId1)) {
                        this.ecology.crossDialectInfluence.set(dialectId1, new Map());
                    }
                    this.ecology.crossDialectInfluence.get(dialectId1)!.set(dialectId2, influence);
                }
            }
        }
    }
    
    private calculateDialectInfluence(dialect1: LivingDialect, dialect2: LivingDialect): number {
        const sharedConcepts = Array.from(dialect1.concepts.keys()).filter(id => dialect2.concepts.has(id)).length;
        const totalConcepts = Math.max(dialect1.concepts.size, dialect2.concepts.size);
        return sharedConcepts / totalConcepts;
    }
    
    private recordLanguageEvolution(evolution?: LanguageEvolution): void {
        if (evolution) {
            this.ecology.languageEvolution.push(evolution);
        }
    }
    
    // Public interface methods
    public getCurrentDialect(): LivingDialect {
        return this.currentDialect;
    }
    
    public getAllDialects(): LivingDialect[] {
        return Array.from(this.ecology.dialects.values());
    }
    
    public getGlobalConcepts(): LivingConcept[] {
        return Array.from(this.ecology.globalConcepts.values());
    }
    
    public getMetaphysicalExpressions(): MetaphysicalExpression[] {
        return Array.from(this.metaphysicalExpressions.values());
    }
    
    public getLanguageEcology(): LanguageEcology {
        return this.ecology;
    }
    
    public generateLivingExpression(context: LanguageContext): string {
        const rules = Array.from(this.currentDialect.grammar.rules.values());
        if (rules.length === 0) return 'essence flows';
        
        const rule = rules[Math.floor(Math.random() * rules.length)];
        return rule.application('', context);
    }
    
    public getConceptByEssence(essence: string): LivingConcept | undefined {
        for (const concept of this.currentDialect.concepts.values()) {
            if (concept.essence.includes(essence)) {
                return concept;
            }
        }
        return undefined;
    }
    
    public getConceptsByEmotion(emotion: string): LivingConcept[] {
        return Array.from(this.currentDialect.concepts.values()).filter(concept => 
            concept.essence.includes(emotion) || concept.name.includes(emotion)
        );
    }
    
    public getMetaphysicalExpressionByType(type: string): MetaphysicalExpression[] {
        return Array.from(this.metaphysicalExpressions.values()).filter(expr => expr.type === type);
    }
    
    public setCreativity(creativity: number): void {
        this.creativity = Math.max(0, Math.min(1, creativity));
    }
    
    public setEmergence(emergence: number): void {
        this.emergence = Math.max(0, Math.min(1, emergence));
    }
    
    // Access to mathematical language systems
    public getFormalGrammar(): FormalGrammarSystem {
        return this.formalGrammar;
    }
    
    public getPhonologicalFeatures(): PhonologicalFeatureSystem {
        return this.phonologicalFeatures;
    }
    
    public getStatisticalModel(): StatisticalLanguageModel {
        return this.statisticalModel;
    }
    
    public getSemanticSpace(): SemanticVectorSpace {
        return this.semanticSpace;
    }
    
    // Generate using formal grammar
    public generateGrammarWord(length: number = 10): string[] {
        return this.formalGrammar.generateFromRegularGrammar('phonological_processes', length);
    }
    
    // Parse with context-free grammar
    public parseSentence(sentence: string): ParseResult {
        return this.formalGrammar.parseContextFree('basic_syntax', sentence);
    }

    // Generate with unrestricted grammar (Type-0, bounded derivation)
    public generateTransformationalExpressions(maxSteps: number = 8): string[] {
        return this.formalGrammar.generateFromUnrestrictedGrammar('transformational_processes', maxSteps);
    }

    // Apply unrestricted rewrite rules to a sequence
    public transformExpression(input: string, maxSteps: number = 4): string[] {
        return this.formalGrammar.applyUnrestrictedRules('transformational_processes', input, maxSteps);
    }
    
    // Get phonological features for a sound
    public getSoundFeatures(sound: string): DistinctiveFeatureMatrix | undefined {
        return this.phonologicalFeatures.getFeatures(sound);
    }
    
    // Add word to statistical model
    public trackWord(word: string): void {
        this.statisticalModel.addWord(word);
    }
    
    // Get word frequency
    public getWordFrequency(word: string): number {
        return this.statisticalModel.getWordFrequency(word);
    }
    
    // Add concept to semantic space
    public addConceptVector(conceptId: string, name: string, dimensions: number[]): void {
        this.semanticSpace.addConcept('default', conceptId, name, dimensions);
    }
    
    // Calculate semantic similarity
    public calculateConceptSimilarity(conceptId1: string, conceptId2: string): SimilarityMetrics | null {
        return this.semanticSpace.calculateSimilarity('default', conceptId1, conceptId2);
    }

    // Convenience API: apply phonological rules to an input string
    public applyPhonology(input: string, ruleNames?: string[]): string {
        return this.phonologicalFeatures.applyRules(input, ruleNames);
    }
}
